import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import { DomEvent } from 'leaflet';

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import numeral from "numeral";

import { Button, IconButton } from '@mui/material';
import classNames from 'classnames';
import { useMap } from 'react-leaflet';
import { ManualScaleValuesProps, MapDataContext } from '../context/map-data';
import { minMaxShape } from '../data/form-shapes';
import { createGradientBase64 } from '../helpers/canvas';
import { ColourScale } from '../helpers/colours';
import useColourScale from '../hooks/useColourScale';
import useLoggedIn from '../hooks/useLoggedIn';
import styles from "../styles/components/scale.module.scss";
import { CreateForm } from './form-components';
import { LoginPanel } from './login-panel';

const SCALE_HEIGHT = 250

interface ScaleProps {
  currentValue: null |number
  colours: ColourScale | undefined | null
  min: null | number | undefined
  max: null | number | undefined
  units?: string
  displayMaxValue?: number | string
  showExtreme?: boolean
  label?: string | undefined
}
export const Scale = ({
  currentValue, 
  min, 
  max, 
  units = '', 
  colours, 
  displayMaxValue = '', 
  showExtreme = false,
  label,
}: ScaleProps) => {
  
  const [saveCurrentScaleValues, setSaveCurrentScaleValues] = useState<ManualScaleValuesProps | null>(null)
  const [areaExtremes, setAreaExtremes] = useState<{min: number | null, max: number | null}>({min: null, max: null})
  const [editMinAndMax, setEditMinAndMax] = useState(false)
  const [imageFill, setImageFill] = useState('');
  const [isEdited, setIsEdited] = useState(false)
  const [currentScaleValues, setCurrentScaleValues] = useState<ManualScaleValuesProps | null>(null)

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (ref.current) {
      DomEvent.disableClickPropagation(ref.current);
      DomEvent.disableScrollPropagation(ref.current);
      // DomEvent.disableClickPropagation(link);
		  DomEvent.on(ref.current, 'click', DomEvent.stop);
		
    }
    return () => {
      ref.current = null;
    }
  }, [ref]);

  const map = useMap();

  const {
    setShowDynamicScaling,
    setScaleValues,
    zoom,
  } = useContext(MapDataContext);

  const isLoggedIn = useLoggedIn()

  const {
    minMaxAreInverted,
    useReverse, 
    getExtremes,
    showCustomScale,
    useMinLabel,
    useMaxLabel,
    valueFormat
  } = useColourScale();

  const {range, rangeValues} = useMemo(() => {
    if (typeof min === 'number' && typeof max === 'number') {
      const range = Math.abs(max - min)
      
      const steps = 4
      const stepIncrease = range/steps
      const isNegative = min > max
      const rangeValues = [...[...new Array(steps)].map((v, index) => {
        if (isNegative) {
          return min - stepIncrease*index  
        }
        return min + stepIncrease*index
      }), max].reverse()
      
      return {range, rangeValues}
    }
    return {range: 0, rangeValues: []}
  }, [min, max])

  const customScaleIsApplicable = useMemo(() => {
    const settingsZoom  = import.meta.env.VITE_MIN_CUSTOM_ZOOM ? Number(import.meta.env.VITE_MIN_CUSTOM_ZOOM) : 8
    return !!(zoom >= settingsZoom)
  }, [zoom])

  useEffect(() => {
    if (colours) {
      const useColours = !useReverse ? [...Object.values(colours)] : [...Object.values(colours)].reverse()
      // if (minMaxAreInverted) {
      //   useColours=useColours.reverse()
      // }
      const scaleData = createGradientBase64(useColours)
      if (scaleData) {
        setImageFill(scaleData)
      }  
    }
  }, [colours, useReverse, minMaxAreInverted])

  const calculateBottomPx = useCallback((val: number | null, useMin?: number | null, useMax?: number | null ) => {
    const bottomVal = 2
    const topVal = SCALE_HEIGHT +1
    if (typeof useMin !== 'undefined' && val === useMin) return bottomVal

    const isNegative = Number(useMin) > Number(useMax)
    if (isNegative && Number(val) > Number(useMin)) return bottomVal
  
    if (val !== null && typeof min === 'number' && range !== null) {
      const scaledPixelValue = Math.abs((val - min)) / range;
      const v = SCALE_HEIGHT * scaledPixelValue
      const useV = (v > topVal)? topVal : v
      return (useV === 0) ? bottomVal : useV
    }
    return bottomVal
  }, [min, max, range, useReverse, minMaxAreInverted])

  const valBottomPx = useMemo(() => {
    const isNegative = Number(min) > Number(max)
    if (!isNegative && Number(currentValue) < Number(min)) return 2
    return calculateBottomPx(currentValue, min, max)
  }, [currentValue, useReverse, minMaxAreInverted, min, max, range])

  useEffect(() => {
    if (editMinAndMax && map) {
      const bounds = map.getBounds()
      if (!bounds) return;
      const bbox: [number, number, number, number] = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]  
      const execute = async () => {
        const currentAreaExtremes = await getExtremes({bbox, update: false})
        setAreaExtremes(currentAreaExtremes)
      }
      execute()  
    }
  }, [editMinAndMax])

  const setScaleFromValues = useCallback((scaleValuesToSet: ManualScaleValuesProps | null) => {
    if (scaleValuesToSet !== null) {
      const {min, max, show_bands, reverse_colors} = scaleValuesToSet
      let numMin = (min !== null && min !== '') ? Number(min) : null
      let numMax = (max !== null && max !== '') ? Number(max) : null
      numMin = (numMin !== null && !isNaN(numMin)) ? numMin : null
      numMax = (numMax !== null && !isNaN(numMax)) ? numMax : null
      // for example bathymetry is inverted min and max
      if (numMin !== null 
        && numMax !== null 
      ) {
        if (numMin > numMax && !minMaxAreInverted ) {
          const temp = numMin
          numMin = Number(`${numMax}`)
          numMax = Number(`${temp}`)
          setCurrentScaleValues((vals) => {
            return {
              ...vals,
              min: numMin,
              max: numMax,
            }
          })
        } else if (minMaxAreInverted) {
          if (numMin > numMax) {
            setCurrentScaleValues((vals) => {
              return {
                ...vals,
                min: numMax,
                max: numMin,
              }
            })
          } else {
            const temp = numMin
            numMin = Number(`${numMax}`)
            numMax = Number(`${temp}`)
          }
          
        }
      }

      setScaleValues({
        min: numMin,
        max: numMax,
        show_bands, 
        reverse_colors,
      })
    } else {
      setScaleValues(null)
    }
  }, [])

  const onCancel = useCallback((e: React.SyntheticEvent) => {
    e.preventDefault()
    setEditMinAndMax(false)
    setScaleFromValues(saveCurrentScaleValues)
    if (saveCurrentScaleValues) {
      setCurrentScaleValues({...saveCurrentScaleValues} )
    }
  }, [saveCurrentScaleValues, ])

  return (
    <>
      <div className={classNames(styles.scale, 'leaflet-control-container')}>
        {customScaleIsApplicable &&
          <div className={styles.editButtonWrapper}>
            <Button
              className={styles.editButton}
              color="info"
              variant="contained"
              startIcon={<SettingsIcon />}
              onClick={(e: React.SyntheticEvent) => {
                e.preventDefault()
                setEditMinAndMax(i => !i)
                setSaveCurrentScaleValues(currentScaleValues)
              }}>
            </Button>
          </div>
        }
        {label && <div className={styles.label}>{label}{showCustomScale? ` - custom` :``}</div>}
        {(!editMinAndMax || (!customScaleIsApplicable && editMinAndMax)) && 
          <>
            {(useMinLabel && useMaxLabel) ?
              <>
                {rangeValues.length > 0 && 
                  <div className={styles.scaleValues} style={{height: `${SCALE_HEIGHT}px`, top: `-${SCALE_HEIGHT-1}px`}}>
                    {rangeValues.map((val, index) => {
                      if (index === 0) {
                        return <b key={val} style={{bottom: `${calculateBottomPx(val, min)}px`}}><span>{useMaxLabel}</span></b>
                      }
                      if (index === rangeValues.length -1) {
                        return <b key={val} style={{bottom: `${calculateBottomPx(val, min)}px`}}><span>{useMinLabel}</span></b>
                      }
                      return null
                    })}
                  </div>
                }
              </>
              :
              <>
                {rangeValues.length > 0 && 
                  <div className={styles.scaleValues} style={{height: `${SCALE_HEIGHT}px`, top: `-${SCALE_HEIGHT-1}px`}}>
                    {rangeValues.map(val => {
                      return <b key={val} style={{bottom: `${calculateBottomPx(val, min)}px`}}><span>{numeral(val).format("0,0.0")}</span></b>
                    })}
                  </div>
                }
              </>
            }
            {max !== null && showExtreme &&
              <div className={styles.maxValue}>{numeral(displayMaxValue || max).format("0,0.00")} {units}<b /></div>
            }
            {imageFill && <img src={imageFill} alt="" width={SCALE_HEIGHT} height={30} />}
            {currentValue !== null && 
              <div className={styles.value} style={{bottom: valBottomPx}}>
                {numeral(currentValue).format(valueFormat)} {units}
                <b />
              </div>
            }
          </>
        }
        {(customScaleIsApplicable && editMinAndMax) && 
        <div ref={ref}>
          {isLoggedIn ?
            <div className={classNames(styles.setup, 'leaflet-control-container')}>
              <IconButton className={styles.closeButton} onClick={onCancel}>
                <CloseIcon />
              </IconButton>
                <h4>Data range</h4>
              <p>This only applies to higher zoom levels.</p>
              <CreateForm
                size="small"
                allowEdit={false}
                shape={minMaxShape}
                defaultValues={currentScaleValues}
                onChange={(vals, key) => {
                  // @ts-ignore
                  setCurrentScaleValues({...vals})
                  setIsEdited(true)
                }}
              />
              {areaExtremes?.min !== null && 
                <p>
                  The visible data range is between {numeral(areaExtremes?.min).format("0,0.00")} and {numeral(areaExtremes?.max).format("0,0.00")} {units}. 
                </p>
              }
              <div className={styles.buttonRow}>
                  <Button
                    role="button"
                    aria-disabled={false}
                    size="small"
                    color="info"
                    startIcon={<CloseIcon />}
                    onClick={(e) => {
                      onCancel(e)
                    }}>
                    Cancel
                  </Button>
                  <Button 
                    size="small"
                    color='success'
                    startIcon={<CheckIcon />}
                    onClick={async () => {
                      setScaleFromValues(currentScaleValues)
                      setShowDynamicScaling(true)
                      setEditMinAndMax(false)
                    }}>
                    Set
                  </Button>
                  <hr />
                  <Button 
                    size="small"
                    color="info"
                    startIcon={<RestartAltIcon />}
                    onClick={() => {
                      setScaleValues(null)
                      setShowDynamicScaling(false)
                      setCurrentScaleValues(null)
                      setEditMinAndMax(false)
                    }}>
                    Reset
                  </Button>
              </div>
            </div>
          :
            <div className={styles.setup}>
              <LoginPanel message={'To use custom scale, please **login** or **create a free account**.'} />
            </div>}
        </div>
        }
      </div>
    </>
  )
}